// ============================================
// Đổi/trả hàng phía nhân viên — sheet 2 mục #55/#56/#59
// ============================================
//
// Contract BE document 03/09/2026. Luồng trạng thái:
//   CREATED ─┬─> APPROVED ────────> ITEMS_RECEIVED ──> REFUND_SENT ──> REFUNDED
//            └─> PENDING_APPROVAL ─┘
//                                  └─> REJECTED
//   + CANCELLED (khách huỷ), EXPIRED (quét tự động)
//
// Chỉ APPROVED mới receive được. Chỉ ITEMS_RECEIVED mới refund được.
// CREATED là trạng thái thoáng qua — policy chạy ngay lúc tạo nên response
// khách nhận đã là APPROVED hoặc PENDING_APPROVAL.
//
// Hậu xử lý sau hoàn tiền (đảo điểm, khôi phục tồn, đổi trạng thái đơn, push)
// do BE làm HẾT — FE không gọi gì thêm (mục #56 của sheet).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { ReturnRequest, UUID } from '@conai/shared-types';
import type { ReceiveReturnItemInput, ReturnStaffListParams } from '@conai/api-client';
import { useReturnsService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';

/**
 * `store_id` ở đây là BỘ LỌC, KHÔNG phải quyền.
 *
 * BE vá 03/09/2026: trước đó `/returns/staff/` chỉ giới hạn theo cửa hàng KHI
 * client tự gửi `store_id` — không gửi thì nhân viên một chi nhánh thấy toàn
 * bộ yêu cầu trả hàng của cả chuỗi, kèm tên khách và giỏ hàng. Giờ BE scope
 * theo `store_scopes` trước, `store_id` chỉ thu hẹp TRONG phần đã được phép.
 *
 * Hệ quả cho FE: đừng bao giờ coi việc gửi `store_id` là cơ chế bảo mật, và
 * cũng đừng hoảng khi bỏ trống — bỏ trống nghĩa là "tất cả cửa hàng TÔI được
 * phép xem", không phải "tất cả cửa hàng".
 */
export function useStaffReturns(filters: ReturnStaffListParams = {}) {
  const returnsService = useReturnsService();

  return useQuery({
    queryKey: queryKeys.returns.list(filters as Record<string, unknown>),
    queryFn: () => returnsService.staffList(filters),
  });
}

/**
 * Tra cứu ở quầy. KHÔNG dùng useQuery vì đây là hành động chủ động của nhân
 * viên (quét/gõ rồi bấm), không phải dữ liệu tự tải.
 */
export function useStaffScanReturn() {
  const returnsService = useReturnsService();

  return useMutation({
    mutationFn: (params: { token?: string; returnCode?: string }) =>
      returnsService.staffScan(params),
  });
}

function useInvalidateReturns() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.returns.all });
}

export function useApproveReturn() {
  const returnsService = useReturnsService();
  const invalidate = useInvalidateReturns();

  return useMutation<ReturnRequest, unknown, UUID>({
    mutationFn: (id) => returnsService.staffApprove(id),
    onSuccess: () => {
      invalidate();
      toast.success('Đã duyệt yêu cầu trả hàng');
    },
  });
}

export function useRejectReturn() {
  const returnsService = useReturnsService();
  const invalidate = useInvalidateReturns();

  return useMutation<ReturnRequest, unknown, { id: UUID; reason: string }>({
    mutationFn: ({ id, reason }) => returnsService.staffReject(id, reason),
    onSuccess: () => {
      invalidate();
      toast.success('Đã từ chối yêu cầu');
    },
  });
}

/**
 * Nhận hàng. `received` chỉ liệt kê dòng THIẾU — dòng không gửi coi như đủ.
 * Response mang `refund_amount` đã tính lại; caller phải hiện số MỚI.
 */
export function useReceiveReturn() {
  const returnsService = useReturnsService();
  const invalidate = useInvalidateReturns();

  return useMutation<ReturnRequest, unknown, { id: UUID; received?: ReceiveReturnItemInput[] }>({
    mutationFn: ({ id, received }) => returnsService.staffReceive(id, received),
    onSuccess: () => {
      invalidate();
      toast.success('Đã nhận hàng vào kho');
    },
  });
}

export function useRefundReturn() {
  const returnsService = useReturnsService();
  const invalidate = useInvalidateReturns();

  return useMutation<ReturnRequest, unknown, UUID>({
    mutationFn: (id) => returnsService.staffRefund(id),
    onSuccess: () => {
      invalidate();
      toast.success('Đã gửi lệnh hoàn tiền');
    },
  });
}
